/* Gallery2 only: present the existing review widget without changing review data. */
(() => {
  'use strict';
  const root = document.querySelector('.gl2-reviews');
  if (!root) return;
  const widget = root.querySelector('#stamped-reviews-widget');
  if (!widget) return;
  const english = root.dataset.language === 'en';
  const locale = english ? 'en-GB' : 'el-GR';
  const loading = root.querySelector('.gl2-reviews-loading');
  const fallback = root.querySelector('.gl2-reviews-fallback');
  const status = root.querySelector('.gl2-reviews-status');
  const heading = root.querySelector('#gl2-review-feed-title');
  const enhancedCards = new WeakSet();
  const enhancedControls = new WeakSet();
  const enhancedTotals = new WeakSet();
  let queued = false;
  let pendingPage = null;
  let fallbackTimer = window.setTimeout(() => {
    if (!widget.querySelector('.stamped-ratings-wrapper')) {
      loading.hidden = true;
      fallback.hidden = false;
    }
  }, 15000);

  function enhanceCard(card) {
    if (enhancedCards.has(card)) return;
    enhancedCards.add(card);
    const rating = card.querySelector('.stamped-reviews-rating');
    if (rating) {
      const stars = rating.querySelectorAll('.stamped-fa-star').length;
      rating.setAttribute('role', 'img');
      rating.setAttribute('aria-label', english ? stars + ' out of 5 stars' : stars + ' από 5 αστέρια');
      rating.querySelectorAll('i').forEach(icon => icon.setAttribute('aria-hidden', 'true'));
    }
    const date = card.querySelector('.stamped-reviews-date');
    const match = date && date.textContent.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const time = document.createElement('time');
      const iso = match[3] + '-' + match[1].padStart(2, '0') + '-' + match[2].padStart(2, '0');
      time.dateTime = iso;
      time.textContent = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(iso + 'T12:00:00Z'));
      date.replaceChildren(time);
    }
    const message = card.querySelector('.stamped-reviews-message');
    if (message && message.textContent.trim().length > 240) {
      const button = document.createElement('button');
      message.id = card.id + '-message';
      message.dataset.collapsed = 'true';
      button.className = 'gl2-review-more';
      button.type = 'button';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', message.id);
      button.textContent = english ? 'Read more' : 'Διάβασε περισσότερα';
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        message.dataset.collapsed = String(!expanded);
        button.textContent = expanded ? (english ? 'Show less' : 'Λιγότερα') : (english ? 'Read more' : 'Διάβασε περισσότερα');
      });
      message.after(button);
    }
    const product = card.querySelector('.stamped-review-product');
    const image = card.querySelector('.stamped-reviews-image img');
    if (image) {
      image.alt = product ? product.textContent.trim() : (english ? 'Reviewed product' : 'Προϊόν αξιολόγησης');
      image.loading = 'lazy';
      image.decoding = 'async';
    }
    card.querySelectorAll('.stamped-reviews-message-image-block a:not([href])').forEach(link => {
      link.tabIndex = 0;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-label', english ? 'Open customer photo or video' : 'Άνοιγμα φωτογραφίας ή βίντεο πελάτη');
      link.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          link.click();
        }
      });
    });
  }

  function enhance() {
    queued = false;
    const cards = widget.querySelectorAll('.stamped-ratings-wrapper');
    if (cards.length) {
      loading.hidden = true;
      fallback.hidden = true;
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
      cards.forEach(enhanceCard);
      cards.forEach(card => {
        const message = card.querySelector('.stamped-reviews-message');
        const button = card.querySelector('.gl2-review-more');
        if (message && button) {
          const fourLines = parseFloat(getComputedStyle(message).lineHeight) * 4;
          button.hidden = message.scrollHeight <= fourLines + 2;
        }
      });
    }
    const total = widget.querySelector('.stamped-widget-text');
    if (total && !enhancedTotals.has(total)) {
      enhancedTotals.add(total);
      const count = total.textContent.match(/[\d,]+/);
      if (count) {
        const number = Number(count[0].replace(/,/g, ''));
        if (Number.isFinite(number)) total.textContent = new Intl.NumberFormat(locale).format(number) + (english ? ' customer reviews' : ' αξιολογήσεις πελατών');
      }
    }
    const pagination = widget.querySelector('.stamped-pagination');
    if (pagination) pagination.setAttribute('aria-label', english ? 'Review pages' : 'Σελίδες αξιολογήσεων');
    widget.querySelectorAll('.stamped-pagination a[data-page]').forEach(link => {
      if (enhancedControls.has(link)) return;
      enhancedControls.add(link);
      link.setAttribute('role', 'button');
      link.tabIndex = 0;
      const isPrev = Boolean(link.closest('#stamped-pagination-prev'));
      const isNext = Boolean(link.closest('#stamped-pagination-next'));
      const current = Boolean(link.closest('.is-active'));
      const label = isPrev ? (english ? 'Previous reviews' : 'Προηγούμενες αξιολογήσεις') : isNext ? (english ? 'Next reviews' : 'Επόμενες αξιολογήσεις') : (english ? 'Page ' : 'Σελίδα ') + link.dataset.page;
      link.setAttribute('aria-label', label);
      if (current) link.setAttribute('aria-current', 'page');
    });
    if (pendingPage && cards.length && cards[0].id !== pendingPage.firstId) {
      const page = widget.getAttribute('data-page') || pendingPage.page;
      status.textContent = english ? 'Review page ' + page + ' loaded.' : 'Φορτώθηκε η σελίδα ' + page + ' των αξιολογήσεων.';
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ behavior: 'auto', block: 'start' });
      pendingPage = null;
    }
  }

  function queueEnhance() {
    if (!queued) {
      queued = true;
      window.requestAnimationFrame(enhance);
    }
  }
  function rememberPage(event) {
    const target = event.target instanceof Element ? event.target.closest('.stamped-pagination a[data-page]') : null;
    if (!target || target.closest('.is-active')) return;
    if (event.type === 'keypress' && event.key !== 'Enter' && event.key !== ' ') return;
    pendingPage = { page: target.dataset.page, firstId: widget.querySelector('.stamped-ratings-wrapper')?.id };
    status.textContent = english ? 'Loading reviews…' : 'Φόρτωση αξιολογήσεων…';
  }
  widget.addEventListener('click', rememberPage, true);
  widget.addEventListener('keypress', rememberPage, true);
  new MutationObserver(queueEnhance).observe(widget, { childList: true, subtree: true });
  if ('ResizeObserver' in window) {
    let lastWidth = widget.clientWidth;
    new ResizeObserver(() => {
      if (widget.clientWidth !== lastWidth) {
        lastWidth = widget.clientWidth;
        queueEnhance();
      }
    }).observe(widget);
  }
  enhance();
})();
